import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  PrinterIcon,
  PlusIcon,
  TrashIcon,
  EyeIcon,
  DocumentDuplicateIcon,
  BookOpenIcon,
  ArrowTopRightOnSquareIcon,
  ClipboardDocumentCheckIcon,
  DocumentTextIcon,
  UsersIcon,
  CheckCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { useUserHasAccess } from "../../../../routes/roleOnly";
import {
  getProjectHandbook,
  getAvailableHandbookDocuments,
  getHandbookDocument,
  addHandbookDocuments,
  importHandbookDocuments,
  removeHandbookDocument,
  createHandbookOverride,
  printProjectHandbook,
} from "../../../../api/hse/projectHandbook";
import { createChecklistFromHseDocument } from "../../../../api/hse/documents";
import { createDocumentForm } from "../../../../api/documentForms";
import { DocumentFileContent } from "../../../../pages/dashboard/hse-ks/documents/DocumentFileContent";

// Promang module detail-view design tokens — see promang-docs PROMANG_BRAND_DESIGN.md (§10/§11)
const C = {
  accent: "#1463C7",
  ink: "#0F172A",
  body: "#475569",
  muted: "#64748B",
  faint: "#94A3B8",
  line: "#E2E8F0",
  line2: "#EEF1F5",
  bg: "#F8FAFC",
  soft: "#EAF3FD",
};

const AVATAR_COLORS = ["#4F46E5", "#0D9488", "#D946EF", "#F59E0B", "#0EA5E9", "#10B981", "#1463C7"];
const avatarColor = (key) => {
  const s = String(key ?? "");
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
};

const cardStyle = {
  background: "#fff",
  border: `1px solid ${C.line}`,
  borderRadius: 16,
  boxShadow: "0 1px 2px rgba(16,28,46,.05)",
  overflow: "hidden",
};
const cardHeadStyle = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "13px 16px",
  borderBottom: `1px solid ${C.line2}`,
};
const iconTileStyle = (color, bg) => ({
  width: 30,
  height: 30,
  borderRadius: 9,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: bg,
  color,
  flex: "0 0 auto",
});

const btnStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 7,
  padding: "9px 14px",
  borderRadius: 10,
  border: `1px solid ${C.line}`,
  background: "#fff",
  color: C.body,
  fontSize: 13.5,
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "inherit",
  whiteSpace: "nowrap",
};
const btnPrimaryStyle = (disabled) =>
  disabled
    ? { ...btnStyle, border: "1px solid transparent", background: C.line, color: C.faint, cursor: "not-allowed" }
    : {
        ...btnStyle,
        border: `1px solid ${C.accent}`,
        background: C.accent,
        color: "#fff",
        fontWeight: 700,
        boxShadow: "0 1px 2px rgba(20,99,199,.3)",
      };
const btnSmallStyle = (variant = "neutral") => {
  const base = { ...btnStyle, padding: "7px 11px", fontSize: 12.5, gap: 6 };
  if (variant === "accent") return { ...base, border: `1px solid ${C.accent}`, color: C.accent, background: "#fff" };
  if (variant === "danger") return { ...base, border: "1px solid #FECACA", color: "#DC2626", background: "#fff" };
  return base;
};

const tabStyle = (active) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 7,
  padding: "7px 12px",
  borderRadius: 9,
  border: "none",
  fontSize: 13,
  fontWeight: 700,
  cursor: "pointer",
  fontFamily: "inherit",
  background: active ? C.ink : "transparent",
  color: active ? "#fff" : C.muted,
});

const SECTION_TABS = [
  { id: "document", labelKey: "project.hse_handbook.section_documents", icon: DocumentTextIcon, color: C.accent, bg: C.soft },
  { id: "checklist", labelKey: "project.hse_handbook.section_checklists", icon: ClipboardDocumentCheckIcon, color: "#15924E", bg: "#E2F3E8" },
  { id: "form", labelKey: "project.hse_handbook.section_forms", icon: DocumentTextIcon, color: "#C2710C", bg: "#FBEFD9" },
  { id: "crew", labelKey: "project.hse_handbook.section_crew", icon: UsersIcon, color: "#7C3AED", bg: "#F1E9FE" },
];

const Pill = ({ children, variant = "neutral" }) => {
  const variants = {
    neutral: { background: C.line2, color: C.muted },
    blue: { background: C.soft, color: C.accent },
    success: { background: "#E2F3E8", color: "#15924E" },
  };
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 10px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        ...variants[variant],
      }}
    >
      {children}
    </span>
  );
};

export const ProjectHseHandbookTab = ({ project }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const projectId = project?.id;

  const canManage = useUserHasAccess({ role: "hse_ks_documents", action: "create" });

  const [loading, setLoading] = useState(true);
  const [handbook, setHandbook] = useState(null);
  const [activeSection, setActiveSection] = useState("document");
  const [busyId, setBusyId] = useState(null);
  const [printing, setPrinting] = useState(false);

  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerLoading, setViewerLoading] = useState(false);
  const [viewerDoc, setViewerDoc] = useState(null);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [available, setAvailable] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [pickerSearch, setPickerSearch] = useState("");

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const data = await getProjectHandbook(projectId);
      setHandbook(data);
    } catch (error) {
      console.error("Error loading project handbook:", error);
      toast.error(t("project.hse_handbook.load_failed"));
    } finally {
      setLoading(false);
    }
  }, [projectId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const entries = handbook?.entries || [];
  const crew = handbook?.crew || [];

  const entriesBySection = useMemo(() => {
    const map = { document: [], checklist: [], form: [] };
    entries.forEach((entry) => {
      const section = map[entry.section] ? entry.section : "document";
      map[section].push(entry);
    });
    return map;
  }, [entries]);

  const handlePrint = async () => {
    setPrinting(true);
    try {
      const res = await printProjectHandbook(projectId);
      if (res?.pdf_url) {
        window.open(res.pdf_url, "_blank");
        toast.success(t("project.hse_handbook.printing"));
      } else {
        toast.error(res?.errors?.[0] || t("project.hse_handbook.print_failed"));
      }
    } catch (error) {
      toast.error(error?.response?.data?.errors?.[0] || t("project.hse_handbook.print_failed"));
    } finally {
      setPrinting(false);
    }
  };

  const handleImportAll = async () => {
    if (!window.confirm(t("project.hse_handbook.import_all_confirm"))) return;
    setBusyId("import");
    try {
      const res = await importHandbookDocuments(projectId, {});
      toast.success(t("project.hse_handbook.imported", { count: res?.added ?? 0 }));
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.errors?.[0] || t("project.hse_handbook.import_failed"));
    } finally {
      setBusyId(null);
    }
  };

  const openPicker = async () => {
    setPickerOpen(true);
    setPickerLoading(true);
    setSelectedIds([]);
    setPickerSearch("");
    try {
      const res = await getAvailableHandbookDocuments(projectId);
      setAvailable(res?.documents || []);
    } catch (error) {
      console.error("Error loading available documents:", error);
      toast.error(t("project.hse_handbook.load_failed"));
    } finally {
      setPickerLoading(false);
    }
  };

  const togglePick = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleAddSelected = async () => {
    if (selectedIds.length === 0) return;
    setBusyId("add");
    try {
      const res = await addHandbookDocuments(projectId, selectedIds);
      toast.success(t("project.hse_handbook.added", { count: res?.added ?? selectedIds.length }));
      setPickerOpen(false);
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.errors?.[0] || t("project.hse_handbook.add_failed"));
    } finally {
      setBusyId(null);
    }
  };

  const handleRemove = async (entry) => {
    if (!window.confirm(t("project.hse_handbook.remove_confirm"))) return;
    setBusyId(entry.id);
    try {
      await removeHandbookDocument(projectId, entry.id);
      toast.success(t("project.hse_handbook.removed"));
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.errors?.[0] || t("project.hse_handbook.remove_failed"));
    } finally {
      setBusyId(null);
    }
  };

  const handleOverride = async (entry) => {
    setBusyId(entry.id);
    try {
      const res = await createHandbookOverride(projectId, entry.id);
      if (res?.errors) {
        toast.error(res.errors[0]);
      } else {
        toast.success(t("project.hse_handbook.override_created"));
        await load();
      }
    } catch (error) {
      toast.error(error?.response?.data?.errors?.[0] || t("project.hse_handbook.override_failed"));
    } finally {
      setBusyId(null);
    }
  };

  const openViewer = async (entry) => {
    setViewerOpen(true);
    setViewerLoading(true);
    setViewerDoc(null);
    try {
      const res = await getHandbookDocument(projectId, entry.id);
      setViewerDoc(res?.document || null);
    } catch (error) {
      console.error("Error loading document:", error);
      toast.error(t("project.hse_handbook.load_failed"));
      setViewerOpen(false);
    } finally {
      setViewerLoading(false);
    }
  };

  const handleCreateChecklist = async (entry) => {
    setBusyId(entry.id);
    try {
      const res = await createChecklistFromHseDocument(entry.document.id, { project_id: projectId });
      if (res?.errors) {
        toast.error(Array.isArray(res.errors) ? res.errors.join(", ") : res.errors);
      } else {
        toast.success(t("project.hse_handbook.checklist_created"));
      }
    } catch (error) {
      toast.error(error?.response?.data?.errors?.[0] || t("project.hse_handbook.checklist_failed"));
    } finally {
      setBusyId(null);
    }
  };

  const handleFillForm = async (entry) => {
    const templateId = entry.document?.fillable_template?.id;
    if (!templateId) {
      toast.error(t("project.hse_handbook.form_failed"));
      return;
    }
    setBusyId(entry.id);
    try {
      const res = await createDocumentForm({
        document_form_template_id: Number(templateId),
        title: entry.document?.title || undefined,
        project_id: projectId,
      });
      const formId = res?.id ?? res?.data?.id;
      if (res?.errors || !formId) {
        toast.error(
          res?.errors
            ? Array.isArray(res.errors)
              ? res.errors.join(", ")
              : res.errors
            : t("project.hse_handbook.form_failed")
        );
        return;
      }
      toast.success(t("project.hse_handbook.form_created"));
      navigate(`/dashboard/hse-ks/documents/form-archive/${formId}`);
    } catch (error) {
      toast.error(error?.response?.data?.errors?.[0] || t("project.hse_handbook.form_failed"));
    } finally {
      setBusyId(null);
    }
  };

  const goToPersonalHandbook = () => {
    navigate(handbook?.personalhandbok?.path || "/dashboard/quality/employee-handbook");
  };

  if (loading) {
    return (
      <div style={{ display: "flex", minHeight: 220, alignItems: "center", justifyContent: "center" }}>
        <ResourceLoading />
      </div>
    );
  }

  const activeMeta = SECTION_TABS.find((s) => s.id === activeSection) || SECTION_TABS[0];
  const ActiveIcon = activeMeta.icon;
  const sectionCount = activeSection === "crew" ? crew.length : (entriesBySection[activeSection] || []).length;

  const filteredAvailable = available.filter((doc) => {
    if (!pickerSearch) return true;
    const q = pickerSearch.toLowerCase();
    return (
      doc.title?.toLowerCase().includes(q) ||
      doc.document_number?.toLowerCase().includes(q) ||
      doc.category?.name?.toLowerCase().includes(q)
    );
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Hero header */}
      <div style={{ ...cardStyle, padding: 16, display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, minWidth: 0 }}>
          <span style={iconTileStyle(C.accent, C.soft)}>
            <BookOpenIcon style={{ width: 18, height: 18 }} />
          </span>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <Pill variant="blue">{t("project.hse_handbook.tab_title")}</Pill>
              <span style={{ fontSize: 12.5, fontWeight: 700, color: C.muted }}>
                {entries.length} {t("project.hse_handbook.section_documents").toLowerCase()} · {crew.length} {t("project.hse_handbook.section_crew").toLowerCase()}
              </span>
            </div>
            <h2 style={{ margin: "6px 0 0", fontSize: 18, fontWeight: 800, letterSpacing: "-.01em", color: C.ink }}>
              {handbook?.title || t("project.hse_handbook.title")}
            </h2>
            <p style={{ margin: "2px 0 0", fontSize: 13, color: C.muted }}>{t("project.hse_handbook.subtitle")}</p>
          </div>
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
          <button type="button" onClick={goToPersonalHandbook} style={btnStyle}>
            <ArrowTopRightOnSquareIcon style={{ width: 16, height: 16 }} />
            {t("project.hse_handbook.open_personal_handbook")}
          </button>
          {canManage && (
            <>
              <button type="button" onClick={openPicker} style={btnStyle}>
                <PlusIcon style={{ width: 16, height: 16 }} />
                {t("project.hse_handbook.add_document")}
              </button>
              <button
                type="button"
                onClick={handleImportAll}
                disabled={busyId === "import"}
                style={{ ...btnStyle, opacity: busyId === "import" ? 0.6 : 1 }}
              >
                <DocumentDuplicateIcon style={{ width: 16, height: 16 }} />
                {t("project.hse_handbook.import_template")}
              </button>
            </>
          )}
          <button
            type="button"
            onClick={handlePrint}
            disabled={printing || entries.length === 0}
            style={btnPrimaryStyle(printing || entries.length === 0)}
          >
            <PrinterIcon style={{ width: 16, height: 16 }} />
            {t("project.hse_handbook.print")}
          </button>
        </div>
      </div>

      {/* View tabs */}
      <div style={{ ...cardStyle, padding: 6, display: "flex", gap: 4, flexWrap: "wrap" }}>
        {SECTION_TABS.map((tab) => {
          const Icon = tab.icon;
          const count = tab.id === "crew" ? crew.length : (entriesBySection[tab.id] || []).length;
          const isActive = activeSection === tab.id;
          return (
            <button key={tab.id} type="button" onClick={() => setActiveSection(tab.id)} style={tabStyle(isActive)}>
              <Icon style={{ width: 15, height: 15 }} />
              {t(tab.labelKey)}
              <span
                style={{
                  borderRadius: 999,
                  padding: "0 7px",
                  fontSize: 11,
                  fontWeight: 800,
                  background: isActive ? "rgba(255,255,255,.22)" : C.line2,
                  color: isActive ? "#fff" : C.muted,
                }}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Section card */}
      <div style={cardStyle}>
        <div style={cardHeadStyle}>
          <span style={iconTileStyle(activeMeta.color, activeMeta.bg)}>
            <ActiveIcon style={{ width: 16, height: 16 }} />
          </span>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: C.ink }}>{t(activeMeta.labelKey)}</h3>
          <span style={{ fontSize: 12.5, color: C.faint, fontWeight: 600 }}>· {sectionCount}</span>
          <span style={{ flex: 1 }} />
          {canManage && activeSection !== "crew" && (
            <button type="button" onClick={openPicker} style={btnSmallStyle("neutral")}>
              <PlusIcon style={{ width: 15, height: 15 }} />
              {t("project.hse_handbook.add_document")}
            </button>
          )}
        </div>

        {activeSection === "crew" ? (
          <CrewList crew={crew} t={t} />
        ) : (
          <DocumentList
            entries={entriesBySection[activeSection] || []}
            section={activeSection}
            canManage={canManage}
            busyId={busyId}
            t={t}
            onView={openViewer}
            onRemove={handleRemove}
            onOverride={handleOverride}
            onCreateChecklist={handleCreateChecklist}
            onFillForm={handleFillForm}
          />
        )}
      </div>

      {/* Document viewer drawer */}
      <Drawer
        open={viewerOpen}
        onClose={() => setViewerOpen(false)}
        title={viewerDoc?.title || t("project.hse_handbook.view")}
        subtitle={[viewerDoc?.document_number, viewerDoc?.category?.name].filter(Boolean).join(" · ")}
        icon={<DocumentTextIcon style={{ width: 18, height: 18 }} />}
      >
        {viewerLoading ? (
          <div style={{ display: "flex", minHeight: 160, alignItems: "center", justifyContent: "center" }}>
            <ResourceLoading />
          </div>
        ) : viewerDoc ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {viewerDoc.is_override && (
              <div>
                <Pill variant="blue">{t("project.hse_handbook.project_copy")}</Pill>
              </div>
            )}
            <div style={cardStyle}>
              <div style={{ padding: 16 }}>
                {viewerDoc.content_type === "file" || viewerDoc.attached_document_id ? (
                  <DocumentFileContent attachedDocumentId={viewerDoc.attached_document_id} />
                ) : viewerDoc.content ? (
                  <div
                    className="prose max-w-none"
                    style={{ fontSize: 14, lineHeight: 1.7, color: C.ink }}
                    dangerouslySetInnerHTML={{ __html: viewerDoc.content }}
                  />
                ) : (
                  <p style={{ fontSize: 14, color: C.muted, margin: 0 }}>{t("project.hse_handbook.no_content")}</p>
                )}
              </div>
            </div>
          </div>
        ) : null}
      </Drawer>

      {/* Add-document picker drawer */}
      <Drawer
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        title={t("project.hse_handbook.add_document")}
        subtitle={handbook?.title}
        icon={<PlusIcon style={{ width: 18, height: 18 }} />}
        footer={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ flex: 1 }} />
            <button type="button" onClick={() => setPickerOpen(false)} style={btnStyle}>
              {t("Cancel")}
            </button>
            <button
              type="button"
              onClick={handleAddSelected}
              disabled={selectedIds.length === 0 || busyId === "add"}
              style={btnPrimaryStyle(selectedIds.length === 0 || busyId === "add")}
            >
              {t("project.hse_handbook.add_selected", { count: selectedIds.length })}
            </button>
          </div>
        }
      >
        <input
          type="text"
          value={pickerSearch}
          onChange={(e) => setPickerSearch(e.target.value)}
          placeholder={t("Search")}
          style={{
            width: "100%",
            marginBottom: 12,
            padding: "10px 12px",
            border: `1px solid ${C.line}`,
            borderRadius: 10,
            fontSize: 14,
            color: C.ink,
            background: "#fff",
            outline: "none",
            fontFamily: "inherit",
          }}
        />
        {pickerLoading ? (
          <div style={{ display: "flex", minHeight: 160, alignItems: "center", justifyContent: "center" }}>
            <ResourceLoading />
          </div>
        ) : filteredAvailable.length === 0 ? (
          <p style={{ padding: "24px 0", textAlign: "center", fontSize: 14, color: C.muted }}>
            {t("project.hse_handbook.no_available_documents")}
          </p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {filteredAvailable.map((doc) => {
              const disabled = doc.in_handbook;
              const checked = selectedIds.includes(doc.id);
              return (
                <div
                  key={doc.id}
                  onClick={() => !disabled && togglePick(doc.id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "11px 12px",
                    border: `1px solid ${checked ? C.accent : C.line}`,
                    borderRadius: 12,
                    background: checked ? C.soft : "#fff",
                    cursor: disabled ? "not-allowed" : "pointer",
                    opacity: disabled ? 0.55 : 1,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={disabled}
                    onChange={() => togglePick(doc.id)}
                    onClick={(e) => e.stopPropagation()}
                    style={{ width: 16, height: 16, accentColor: C.accent }}
                  />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {doc.title}
                    </p>
                    <p style={{ margin: "2px 0 0", fontSize: 12, color: C.faint, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {[doc.document_number, doc.category?.name, doc.document_type_display].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                  {disabled && <span style={{ fontSize: 12, color: C.faint, flex: "0 0 auto" }}>{t("project.hse_handbook.already_added")}</span>}
                </div>
              );
            })}
          </div>
        )}
      </Drawer>
    </div>
  );
};

const DocumentList = ({ entries, section, canManage, busyId, t, onView, onRemove, onOverride, onCreateChecklist, onFillForm }) => {
  if (entries.length === 0) {
    return (
      <div style={{ padding: "40px 16px", textAlign: "center", fontSize: 14, color: C.muted }}>
        {t("project.hse_handbook.empty_section")}
      </div>
    );
  }

  return (
    <div>
      {entries.map((entry, idx) => {
        const doc = entry.document || {};
        const busy = busyId === entry.id;
        const dotColor = avatarColor(doc.category?.code || doc.document_number || doc.title);
        return (
          <div
            key={entry.id}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "13px 16px",
              borderTop: idx === 0 ? "none" : `1px solid ${C.line2}`,
            }}
          >
            <span style={{ width: 10, height: 10, borderRadius: 3, background: dotColor, flex: "0 0 auto" }} />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 14.5, fontWeight: 700, color: C.ink }}>{doc.title}</span>
                {entry.is_override && <Pill variant="blue">{t("project.hse_handbook.project_copy")}</Pill>}
              </div>
              <p style={{ margin: "4px 0 0", fontSize: 12.5, color: C.faint }}>
                {[doc.document_number, doc.category?.name, doc.chapter ? `${doc.chapter.number} ${doc.chapter.title}` : null]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, justifyContent: "flex-end" }}>
              <button type="button" onClick={() => onView(entry)} style={btnSmallStyle("neutral")}>
                <EyeIcon style={{ width: 15, height: 15 }} />
                {t("project.hse_handbook.view")}
              </button>

              {section === "checklist" && (
                <button type="button" onClick={() => onCreateChecklist(entry)} disabled={busy} style={{ ...btnSmallStyle("accent"), opacity: busy ? 0.6 : 1 }}>
                  <CheckCircleIcon style={{ width: 15, height: 15 }} />
                  {t("project.hse_handbook.create_checklist")}
                </button>
              )}

              {section === "form" && (
                <button type="button" onClick={() => onFillForm(entry)} disabled={busy} style={{ ...btnSmallStyle("accent"), opacity: busy ? 0.6 : 1 }}>
                  <DocumentTextIcon style={{ width: 15, height: 15 }} />
                  {t("project.hse_handbook.fill_form")}
                </button>
              )}

              {canManage && section === "document" && !entry.is_override && (
                <button type="button" onClick={() => onOverride(entry)} disabled={busy} style={{ ...btnSmallStyle("neutral"), opacity: busy ? 0.6 : 1 }}>
                  <DocumentDuplicateIcon style={{ width: 15, height: 15 }} />
                  {t("project.hse_handbook.create_copy")}
                </button>
              )}

              {canManage && (
                <button type="button" onClick={() => onRemove(entry)} disabled={busy} style={{ ...btnSmallStyle("danger"), opacity: busy ? 0.6 : 1 }}>
                  <TrashIcon style={{ width: 15, height: 15 }} />
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const CrewList = ({ crew, t }) => {
  if (crew.length === 0) {
    return (
      <div style={{ padding: "40px 16px", textAlign: "center", fontSize: 14, color: C.muted }}>
        {t("project.hse_handbook.no_crew")}
      </div>
    );
  }

  return (
    <div>
      {crew.map((member, idx) => {
        const color = avatarColor(member.user_id || member.name);
        const initials = String(member.name || "?")
          .split(" ")
          .map((w) => w[0])
          .filter(Boolean)
          .slice(0, 2)
          .join("")
          .toUpperCase();
        return (
          <div
            key={`${member.user_id}-${member.source}`}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "12px 16px",
              borderTop: idx === 0 ? "none" : `1px solid ${C.line2}`,
            }}
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 36,
                height: 36,
                borderRadius: 999,
                background: color,
                color: "#fff",
                fontSize: 13,
                fontWeight: 800,
                flex: "0 0 auto",
              }}
            >
              {initials}
            </span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <p style={{ margin: 0, fontSize: 14.5, fontWeight: 700, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {member.name || "—"}
                {member.admin ? <span style={{ color: C.faint, fontWeight: 600 }}> · admin</span> : null}
              </p>
              <p style={{ margin: "2px 0 0", fontSize: 12.5, color: C.faint, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {[member.role || (member.source === "schedule" ? t("project.hse_handbook.crew_scheduled") : null), member.email]
                  .filter(Boolean)
                  .join(" · ") || "—"}
              </p>
            </div>
            <div style={{ flex: "0 0 auto" }}>
              {member.on_site ? (
                <Pill variant="success">{t("project.hse_handbook.crew_on_site")}</Pill>
              ) : member.planned ? (
                <Pill variant="blue">{t("project.hse_handbook.crew_planned")}</Pill>
              ) : (
                <Pill variant="neutral">{member.status || t("project.hse_handbook.crew_assigned")}</Pill>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Right-side slide-over drawer (module pattern §10)
const Drawer = ({ open, onClose, title, subtitle, icon, footer, children }) => {
  const [mounted, setMounted] = useState(false);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      const id = requestAnimationFrame(() => setShown(true));
      document.body.style.overflow = "hidden";
      return () => cancelAnimationFrame(id);
    }
    setShown(false);
    const timeout = setTimeout(() => setMounted(false), 280);
    document.body.style.overflow = "";
    return () => clearTimeout(timeout);
  }, [open]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    if (open) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => () => { document.body.style.overflow = ""; }, []);

  if (!mounted) return null;

  return createPortal(
    <div style={{ position: "fixed", inset: 0, zIndex: 100 }}>
      <div
        onClick={onClose}
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(18,24,38,.42)",
          backdropFilter: "blur(2px)",
          opacity: shown ? 1 : 0,
          transition: "opacity .28s ease",
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          width: "min(560px, 100vw)",
          background: "#fff",
          boxShadow: "-12px 0 40px rgba(20,28,46,.18)",
          display: "flex",
          flexDirection: "column",
          transform: shown ? "translateX(0)" : "translateX(100%)",
          transition: "transform .32s cubic-bezier(.22,.9,.25,1)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", borderBottom: `1px solid ${C.line}`, flex: "0 0 auto" }}>
          <span style={iconTileStyle("#fff", C.ink)}>{icon}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {title}
            </div>
            {subtitle ? (
              <div style={{ fontSize: 12, color: C.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{subtitle}</div>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{ ...iconTileStyle(C.muted, C.bg), width: 34, height: 34, border: "none", cursor: "pointer" }}
          >
            <XMarkIcon style={{ width: 18, height: 18 }} />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: 16, background: C.bg }}>{children}</div>

        {footer ? <div style={{ flex: "0 0 auto", padding: "12px 16px", borderTop: `1px solid ${C.line}`, background: "#fff" }}>{footer}</div> : null}
      </div>
    </div>,
    document.body
  );
};

export default ProjectHseHandbookTab;
